import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import BreadCrumb from "../../../components/BreadCrumb";
import "swiper/swiper-bundle.css";
import { Images } from "../../../utils/imageConstants";
import { useQuery } from "@tanstack/react-query";
import { FiEdit2 } from "react-icons/fi";
import type {
  IGetAllTournaments,
  ISearchResponse,
  ITournamentsByLeagueResponse,
} from "../types/home";
import UpdateTournamentModal from "./UpdateTournamentModal";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import { Api } from "../../../services/interceptor";
import { formatDateRange, getTournamentStatus } from "../../../helper/helper";
import useDebounce from "../../../hooks/useDebounce";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../../../redux/hooks";
import {
  setTournamentsTab,
  type TournamentTab,
} from "../../../redux/slice/tournamentsTabSlice";
import { selectSelectedLeague } from "../../../redux/slice/selectedLeagueSlice";
import { getAuthToken, getGuestModeFromStorage } from "../../../helper/helper";
import { canCreateTournamentInLeague } from "../../../utils/leagueAccess";
import { fetchLeagueById } from "../../myLeagues/api/myLeaguesApi";

const TOURNAMENTS_MOBILE_MAX_PX = 768;
const TOURNAMENTS_PAGE_SIZE = 12;

const Tournaments: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [viewList, setViewList] = useState(false);
  const [visibleTournamentCount, setVisibleTournamentCount] = useState(TOURNAMENTS_PAGE_SIZE);
  const [hasAutoPickedInitialTab, setHasAutoPickedInitialTab] = useState(false);
  const [tournamentSearch, setTournamentSearch] = useState("");
  const [tournamentDropdown, setTournamentDropdown] = useState(false);
  const debouncedSearchTerm = useDebounce(tournamentSearch, 500);
  const selectedLeague = useAppSelector(selectSelectedLeague);
  const selectedLeagueId = selectedLeague?.leagueId ?? null;
  const activeTournamentTab = useAppSelector((s) => s.tournamentsTab.value);
  const setTab = (t: TournamentTab) => dispatch(setTournamentsTab(t));
  const token = getAuthToken();
  const isGuestModeRedux = useAppSelector((s) => s.authModal.isGuestMode);
  const isGuestModeStorage = getGuestModeFromStorage();
  const isGuestMode = isGuestModeRedux || isGuestModeStorage;

  // Fallback: when redux selectedLeague is hydrated from a minimal source (e.g. the
  // Skip path of the League create stepper) it may not include canAddTournamentToLeague.
  // Fetch the full league details so the gate works regardless of redux hydration.
  const reduxCanCreate = canCreateTournamentInLeague(selectedLeague);
  const { data: leagueDetailsForGate } = useQuery({
    queryKey: [QUERY_KEY.getLeagueDetails, selectedLeagueId],
    queryFn: () => fetchLeagueById(selectedLeagueId as string),
    enabled: !!selectedLeagueId && !!token && !isGuestMode && !reduxCanCreate,
    staleTime: 5 * 60 * 1000,
  });
  const fetchedCanCreate = !!leagueDetailsForGate?.canAddTournamentToLeague
    || (leagueDetailsForGate?.roleName ?? "").toUpperCase().includes("LEAGUE_ADMIN");
  const showCreateButton =
    !!token && !isGuestMode && (reduxCanCreate || fetchedCanCreate);

  const [tournamentToEdit, setTournamentToEdit] = useState<IGetAllTournaments | null>(null);
  const [isNarrowTournaments, setIsNarrowTournaments] = useState(() =>
    typeof window !== "undefined" &&
    window.matchMedia(`(max-width: ${TOURNAMENTS_MOBILE_MAX_PX}px)`).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${TOURNAMENTS_MOBILE_MAX_PX}px)`);
    const sync = () => setIsNarrowTournaments(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  const openEditTournament = useCallback((t: IGetAllTournaments) => {
    setTournamentToEdit(t);
  }, []);

  const { data: tournaments, isFetchedAfterMount: isTournamentsFetchedAfterMount } = useQuery<ITournamentsByLeagueResponse>({
    queryKey: [QUERY_KEY.getTournamentsByLeagues, selectedLeagueId],
    enabled: !!selectedLeagueId,
    queryFn: async () =>
      (
        await Api.get<ITournamentsByLeagueResponse>(
          `${QUERY_KEY.getTournamentsByLeagues}/${selectedLeagueId}`
        )
      ).data,
  });

  const tournamentsByStatus = useMemo(() => {
    const list = tournaments?.tournamentResponses ?? [];
    return {
      Ongoing: list.filter((t) => getTournamentStatus(t.startDate, t.endDate) === "Ongoing")
        .length,
      Upcoming: list.filter((t) => getTournamentStatus(t.startDate, t.endDate) === "Upcoming")
        .length,
      Completed: list.filter((t) => getTournamentStatus(t.startDate, t.endDate) === "Completed")
        .length,
    } as const;
  }, [tournaments?.tournamentResponses]);

  useEffect(() => {
    if (!selectedLeagueId || isNarrowTournaments) return;
    if (hasAutoPickedInitialTab) return;
    if (!isTournamentsFetchedAfterMount) return;

    const statusOrder: TournamentTab[] = ["Ongoing", "Upcoming", "Completed"];
    const tabsWithData = statusOrder.filter((tab) => tournamentsByStatus[tab] > 0);

    if (tabsWithData.length === 0 || tabsWithData.length === statusOrder.length) {
      if (activeTournamentTab !== "Ongoing") {
        setTab("Ongoing");
      }
      setHasAutoPickedInitialTab(true);
      return;
    }

    const firstAvailableTab = tabsWithData[0];
    if (firstAvailableTab && activeTournamentTab !== firstAvailableTab) {
      setTab(firstAvailableTab);
    }
    setHasAutoPickedInitialTab(true);
  }, [
    activeTournamentTab,
    hasAutoPickedInitialTab,
    isTournamentsFetchedAfterMount,
    isNarrowTournaments,
    selectedLeagueId,
    tournamentsByStatus,
  ]);

  useEffect(() => {
    setHasAutoPickedInitialTab(false);
    setTab("Ongoing");
  }, [selectedLeagueId]);

  const { isLoading, data: searchedData } = useQuery<ISearchResponse[]>({
    queryKey: [QUERY_KEY.websearch, debouncedSearchTerm],
    enabled: !!debouncedSearchTerm,
    queryFn: async () => {
      const response = await Api.get(QUERY_KEY.websearch, {
        params: {
          name: debouncedSearchTerm,
        },
      });
      const data = await response?.data;
      return data?.tournamentResponses ?? [];
    },
  });

  useEffect(() => {
    if (!tournamentDropdown) return;

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      const inside = containerRef.current?.contains(target ?? null);
      if (!inside) {
        setTournamentDropdown(false);
        setTournamentSearch("");
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setTournamentDropdown(false);
        setTournamentSearch("");
      }
    };

    // use 'pointerdown' for all inputs (mouse, touch, pen)
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [tournamentDropdown]);

  const filteredTournaments = useMemo(() => {
    const list = tournaments?.tournamentResponses ?? [];
    if (isNarrowTournaments) return list;
    return list.filter(
      (t) => getTournamentStatus(t.startDate, t.endDate) === activeTournamentTab
    );
  }, [tournaments?.tournamentResponses, isNarrowTournaments, activeTournamentTab]);

  const displayedTournaments = useMemo(
    () => filteredTournaments.slice(0, visibleTournamentCount),
    [filteredTournaments, visibleTournamentCount]
  );

  const canShowMoreTournaments = displayedTournaments.length < filteredTournaments.length;

  useEffect(() => {
    setVisibleTournamentCount(TOURNAMENTS_PAGE_SIZE);
  }, [activeTournamentTab, selectedLeagueId, tournamentSearch]);

  const effectiveViewList = isNarrowTournaments ? false : viewList;

  return (
    <div>
      <div className="main-tournament-wrap">
        <div className="custom-container main-middle-content-wrap">
          <BreadCrumb />
          <div
            className={`tournaments-wrapper-container ${filteredTournaments.length === 0
              ? "tournaments-wrapper-container--empty"
              : "tournaments-wrapper-container--filled"
              }`}
          >
            <div className="tournaments-main-heading-wrapper">
              {/* <h1 className="heading">Tournaments</h1> */}
              <div className="right-wrapper">


              </div>
            </div>
            <div
              className="tournaments-tab-wrapper"
              aria-hidden={isNarrowTournaments}
            >
              <div className="tournaments-tab-scroll" role="tablist" aria-label="Tournament status">
                <button
                  type="button"
                  className={`tab ${activeTournamentTab === "Ongoing" ? "active" : ""
                    }`}
                  role="tab"
                  aria-selected={activeTournamentTab === "Ongoing"}
                  onClick={() => {
                    setHasAutoPickedInitialTab(true);
                    setTab("Ongoing");
                  }}
                >
                  Ongoing
                </button>
                <button
                  type="button"
                  className={`tab ${activeTournamentTab === "Upcoming" ? "active" : ""
                    }`}
                  role="tab"
                  aria-selected={activeTournamentTab === "Upcoming"}
                  onClick={() => {
                    setHasAutoPickedInitialTab(true);
                    setTab("Upcoming");
                  }}
                >
                  Upcoming
                </button>
                <button
                  type="button"
                  className={`tab ${activeTournamentTab === "Completed" ? "active" : ""
                    }`}
                  role="tab"
                  aria-selected={activeTournamentTab === "Completed"}
                  onClick={() => {
                    setHasAutoPickedInitialTab(true);
                    setTab("Completed");
                  }}
                >
                  Completed
                </button>
              </div>
              <div className="right-icons">
                <div className="tournament-search-wrapper" ref={containerRef}>
                  <img src={Images.dark_search_icon} />
                  <input
                    type="text"
                    placeholder="Search Tournaments..."
                    value={tournamentSearch}
                    onChange={(e) => {
                      setTournamentSearch(e.target.value);
                      setTournamentDropdown(true);
                    }}
                  />
                  {tournamentDropdown && (
                    <div className="search-dropdown" role="listbox">
                      {isLoading ? (
                        <div className="empty">Searching…</div>
                      ) : (searchedData?.length ?? 0) > 0 ? (
                        <ul className="tournaments-names-wrapper">
                          {searchedData!.map((t) => (
                            <li
                              key={t?.tournamentId}
                              className="item"
                              onClick={() => {
                                setTournamentSearch(t.tournamentName);
                                setTournamentDropdown(false);
                              }}
                              role="option"
                            >
                              {t.tournamentName}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="empty">No Tournaments Found</div>
                      )}
                    </div>
                  )}
                </div>
                <div className="view-switch" role="group" aria-label="View mode">
                  <button
                    type="button"
                    className={`tournaments-view-toggle ${!viewList ? "active" : ""}`}
                    onClick={() => setViewList(false)}
                    aria-label="Grid view"
                    aria-pressed={!viewList}
                  >
                    <img src={Images.card_show_icon} alt="" />
                  </button>
                  <button
                    type="button"
                    className={`tournaments-view-toggle ${viewList ? "active" : ""}`}
                    onClick={() => setViewList(true)}
                    aria-label="List view"
                    aria-pressed={viewList}
                  >
                    <img src={Images.list_view_icon} alt="" />
                  </button>
                </div>
                {showCreateButton && (
                  <button
                    className="create-button"
                    onClick={() => navigate("/tournaments/create-tournament")}
                  >
                    <img src={Images.add_icon} /> <span>Create</span>
                  </button>
                )}
              </div>
            </div>

            <div className="tournaments-wrapper">
              {!effectiveViewList &&
                displayedTournaments.length > 0 &&
                displayedTournaments.map((tournament) => (
                  <div
                    className={`tournament-card${tournament.canEditTournament && token && !isGuestMode ? " tournament-card--editable" : ""}`}
                    key={tournament?.tournamentId}
                    onClick={() => {
                      sessionStorage.setItem("tournamentId", tournament?.tournamentId);
                      sessionStorage.setItem("tournamentName", tournament?.tournamentName);
                      if (selectedLeagueId) {
                        navigate(
                          `/my-leagues/${selectedLeagueId}/tournaments/${tournament?.tournamentId}`
                        );
                      } else {
                        navigate("/tournaments/tournaments-details");
                      }
                    }}
                  >
                    {tournament.canEditTournament && token && !isGuestMode && (
                      <button
                        type="button"
                        className="tournament-card-edit-btn"
                        aria-label="Edit tournament"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditTournament(tournament);
                        }}
                      >
                        <FiEdit2 size={18} />
                      </button>
                    )}
                    {/* <div className="tournament-status">
                      <span>
                        {getTournamentStatus(
                          tournament.startDate,
                          tournament.endDate
                        )}
                      </span>
                    </div> */}
                    <div className="card-image">
                      {/* <div className="card-bg-images">
                        <img
                          src={cardRightImage}
                          alt="Tournament"
                          className="main-image right-image"
                        />
                        <img
                          src={cardLeftImage}
                          alt="Tournament"
                          className="main-image left-image"
                        />
                      </div> */}
                      <div className="card-info">
                        <p className="tournament-title">
                          {tournament?.tournamentName || "-"}
                        </p>
                        <p className="tournament-dates">
                          {formatDateRange(
                            tournament?.startDate,
                            tournament?.endDate
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="location-info">
                      <img src={Images.location_card_icon} />
                      <span>{tournaments?.leagueResponse?.city}</span>
                    </div>
                  </div>
                ))}

              {/* Table view */}
              {effectiveViewList && displayedTournaments.length > 0 && (
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Tournaments</th>
                        <th>Date</th>
                        <th>Location</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedTournaments.map((tournament) => (
                        <tr
                          key={tournament.tournamentId}
                          onClick={() => {
                            sessionStorage.setItem("tournamentId", tournament?.tournamentId);
                            sessionStorage.setItem("tournamentName", tournament?.tournamentName);
                            if (selectedLeagueId) {
                              navigate(
                                `/my-leagues/${selectedLeagueId}/tournaments/${tournament?.tournamentId}`
                              );
                            } else {
                              navigate("/tournaments/tournaments-details");
                            }
                          }}
                        >
                          <td>
                            <span className="tournament-table-name-cell">
                              {tournament?.tournamentName || "-"}
                              {tournament.canEditTournament && token && !isGuestMode && (
                                <button
                                  type="button"
                                  className="tournament-table-edit-btn"
                                  aria-label="Edit tournament"
                                  onPointerDown={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openEditTournament(tournament);
                                  }}
                                >
                                  <FiEdit2 size={16} />
                                </button>
                              )}
                            </span>
                          </td>
                          <td>
                            <span className="cell-with-icon">
                              <img src={Images.table_date_icon} alt="Calendar icon" />
                              {formatDateRange(
                                tournament?.startDate,
                                tournament?.endDate
                              )}
                            </span>
                          </td>
                          <td>
                            <span className="cell-with-icon">
                              <img src={Images.location_icon} alt="Location icon" />
                              {tournaments?.leagueResponse?.city}
                            </span>
                          </td>
                          <td className="status live">
                            <span className="cell-with-icon">
                              <img src={Images.table_status_icon} alt="Status icon" />
                              <span className="live-text">
                                {getTournamentStatus(
                                  tournament.startDate,
                                  tournament.endDate
                                )}
                              </span>
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            {canShowMoreTournaments && (
              <div className="tournaments-load-more-wrap">
                <button
                  type="button"
                  className="tournaments-load-more-btn"
                  onClick={() =>
                    setVisibleTournamentCount((prev) => prev + TOURNAMENTS_PAGE_SIZE)
                  }
                >
                  Show more
                </button>
              </div>
            )}
            {filteredTournaments?.length === 0 && (
              <div className="no-data-found">
                <img src={Images.my_leagues_no} alt="No Gallery" />
                <p>
                  {isNarrowTournaments
                    ? "No tournaments found"
                    : `No ${activeTournamentTab.toLowerCase()} tournaments found`}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <UpdateTournamentModal
        key={tournamentToEdit?.tournamentId ?? "tournament-modal-closed"}
        open={!!tournamentToEdit}
        tournament={tournamentToEdit}
        leagueId={selectedLeagueId ?? tournamentToEdit?.leagueId ?? null}
        onOpenChange={(o) => {
          if (!o) setTournamentToEdit(null);
        }}
      />
    </div>
  );
};

export default Tournaments;
